// src/pages/HowItWorks.jsx
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

function StepDot({ active, done, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "group flex items-center gap-2 rounded-full px-3 py-2 transition",
        active ? "bg-white shadow-sm" : "hover:bg-white/60",
      ].join(" ")}
    >
      <span
        className={[
          "grid place-items-center w-7 h-7 rounded-full text-sm font-extrabold border",
          done
            ? "bg-black text-white border-black"
            : active
            ? "bg-white text-black border-black"
            : "bg-white/70 text-black/70 border-black/30",
        ].join(" ")}
      >
        {done ? "✓" : ""}
      </span>

      <span
        className={[
          "text-sm sm:text-base font-extrabold",
          active ? "text-black" : "text-black/70",
        ].join(" ")}
        style={{ fontFamily: "var(--font-head)" }}
      >
        {label}
      </span>
    </button>
  );
}

function DetailsBlock({ summary, children, defaultOpen = false }) {
  return (
    <details
      className="mt-5 bg-white rounded-2xl border shadow-sm"
      open={defaultOpen}
    >
      <summary
        className="cursor-pointer select-none px-5 py-4 font-extrabold text-brand-ink"
        style={{ fontFamily: "var(--font-head)" }}
      >
        {summary}
      </summary>
      <div className="px-5 pb-5">{children}</div>
    </details>
  );
}

export default function HowItWorks() {
  const steps = useMemo(
    () => [
      {
        key: "intro",
        title: "How Once-A-Hunt works",
        subtitle:
          "Once-A-Hunt is a playful scavenger-hunt maker for grown-ups who want learning to feel like a game, not a chore.",
        image: "/gallery/gallery-12.jpg",
        body: (
          <>
            <p className="text-brand-muted text-base sm:text-lg leading-relaxed">
              You answer a few cozy questions about your child and your space, and we turn that into a printable
              adventure: clear instructions for you, a story-like hunt for your child, and a simple answer key so you
              can check the learning.
            </p>

            <DetailsBlock summary="What you get (tap to open)">
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { title: "Made for real life", desc: "Home, classroom, backyard, park — any space works." },
                  { title: "Quick to set up", desc: "A few questions → printable pages in minutes." },
                  { title: "Kid-friendly story", desc: "Reads like an adventure, not a worksheet." },
                  { title: "Easy to check", desc: "Answer key included so you can verify quickly." },
                ].map((c) => (
                  <div key={c.title} className="bg-white/70 rounded-2xl p-4 border">
                    <div className="font-extrabold text-brand-primary" style={{ fontFamily: "var(--font-head)" }}>
                      {c.title}
                    </div>
                    <div className="text-brand-muted mt-1">{c.desc}</div>
                  </div>
                ))}
              </div>
            </DetailsBlock>
          </>
        ),
      },
      {
        key: "step1",
        title: "Step 1 — You answer a short questionnaire",
        subtitle: "These answers personalize the story and the learning steps.",
        image: "/gallery/gallery-02.jpg",
        body: (
          <>
            <DetailsBlock summary="See the questions (tap to open)" defaultOpen>
              <ul className="space-y-3">
                {[
                  "Your hunter’s name",
                  "Your relationship to the child (Mom, Dad, Teacher, etc.)",
                  "Their age range",
                  "Difficulty level",
                  "A couple of places where you want to hide clues",
                ].map((item) => (
                  <li key={item} className="flex gap-3">
                    <span className="mt-1 w-2.5 h-2.5 rounded-full bg-black shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </DetailsBlock>
          </>
        ),
      },
      {
        key: "step2",
        title: "Step 2 — We generate a printable scavenger hunt",
        subtitle: "Once-A-Hunt creates 3 pages just for you.",
        image: "/gallery/gallery-11.jpg",
        body: (
          <DetailsBlock summary="See what’s inside" defaultOpen>
            <div className="grid lg:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-xl border">
                <strong>Parent / Guardian instructions</strong>
                <p className="text-brand-muted mt-1">
                  Explains where to hide each clue and how to label spots.
                </p>
              </div>
              <div className="bg-white p-4 rounded-xl border">
                <strong>Child scavenger hunt page</strong>
                <p className="text-brand-muted mt-1">
                  Friendly instructions that guide your child to find clues and follow the trail.
                </p>
              </div>
              <div className="bg-white p-4 rounded-xl border">
                <strong>Answer key</strong>
                <p className="text-brand-muted mt-1">
                  Shows step answers and the final result for quick checking.
                </p>
              </div>
            </div>
          </DetailsBlock>
        ),
      },
      {
        key: "sample",
        title: "Sample scavenger hunt",
        subtitle: "A preview of what your child experiences.",
        image: "/gallery/gallery-05.jpg",
        body: (
          <DetailsBlock summary="View sample preview" defaultOpen>
            <p className="text-brand-muted">
              “Hi Aria! Your mission starts now. Follow the clues, solve the steps,
              and discover where your surprise is hiding…”
            </p>
          </DetailsBlock>
        ),
      },
    ],
    []
  );

  const [i, setI] = useState(0);
  const last = i === steps.length - 1;
  const step = steps[i];

  return (
    <div className="h-[calc(100vh-140px)] overflow-hidden flex flex-col">
      <section className="hero-sky">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="bg-white/70 border rounded-3xl p-5">
            <div className="flex justify-between items-center">
              <h1 className="text-3xl font-extrabold text-brand-primary" style={{ fontFamily: "var(--font-head)" }}>
                How it works
              </h1>

              <div className="flex gap-2">
                <button
                  onClick={() => setI(Math.max(0, i - 1))}
                  disabled={i === 0}
                  className="rounded-full px-4 py-2 border font-extrabold"
                >
                  Back
                </button>

                {!last ? (
                  <button
                    onClick={() => setI(Math.min(steps.length - 1, i + 1))}
                    className="rounded-full px-4 py-2 bg-black text-white font-extrabold"
                  >
                    Next
                  </button>
                ) : (
                  <Link
                    to="/number-hunt"
                    className="rounded-full px-5 py-2 bg-brand-accent text-white font-extrabold"
                  >
                    Start your hunt now
                  </Link>
                )}
              </div>
            </div>

            <div className="mt-4 flex gap-2 flex-wrap">
              {steps.map((s, idx) => (
                <StepDot
                  key={s.key}
                  active={idx === i}
                  done={idx < i}
                  label={idx === 0 ? "Intro" : idx === steps.length - 1 ? "Sample scavenger hunt" : `Step ${idx}`}
                  onClick={() => setI(idx)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="flex-1">
        <div className="max-w-7xl mx-auto px-4 h-full grid lg:grid-cols-2 gap-5">
          <div className="bg-white/80 border rounded-3xl p-6">
            <h2 className="text-2xl font-extrabold text-brand-primary">{step.title}</h2>
            <p className="text-brand-muted mt-2">{step.subtitle}</p>
            <div className="mt-4">{step.body}</div>
          </div>

          <div className="rounded-3xl overflow-hidden border">
            <img src={step.image} alt={step.title} className="w-full h-full object-cover" />
          </div>
        </div>
      </section>
    </div>
  );
}
